/**
 * Preferência de listagem guardada no navegador (localStorage), por tela.
 *
 * Criado em 07/10/2026 para o "Reverso" do envio em massa: o switch voltava ao
 * padrão a cada abertura da listagem. Navegador sem localStorage (modo privado
 * restrito) cai no padrão, sem erro.
 * 14/10/2026: preferências numéricas (itens por página dos leads da lista).
 */
const PREFIX = 'sf.list-pref.';

export function readBooleanPreference(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(PREFIX + key);

    return value === null ? fallback : value === 'true';
  } catch {
    return fallback;
  }
}

/**
 * Número salvo (ex.: itens por página). Ausente, inválido ou fora de `allowed`
 * → `fallback`. Criado em 14/10/2026 para os leads da lista (M9, pedido do Misael).
 */
export function readNumberPreference(key: string, fallback: number, allowed?: readonly number[]): number {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw === null) {
      return fallback;
    }
    const value = Number(raw);
    if (!Number.isInteger(value) || value <= 0 || (allowed && !allowed.includes(value))) {
      return fallback;
    }
    return value;
  } catch {
    return fallback;
  }
}

export function saveNumberPreference(key: string, value: number): void {
  try {
    localStorage.setItem(PREFIX + key, String(value));
  } catch {
    // sem armazenamento: a preferência vale só nesta tela aberta
  }
}

export function saveBooleanPreference(key: string, value: boolean): void {
  try {
    localStorage.setItem(PREFIX + key, String(value));
  } catch {
    // sem armazenamento: a preferência vale só nesta tela aberta
  }
}

/**
 * Preferência de escolha fechada (09/10/2026, filtro de canal do dashboard).
 *
 * O valor salvo só volta se ainda for uma das opções: uma versão antiga da tela,
 * ou alguém mexendo no localStorage, não deixa a tela num estado que ela não
 * desenha — cai no padrão.
 */
export function readChoicePreference<T extends string>(key: string, options: ReadonlyArray<T>, fallback: T): T {
  try {
    const value = localStorage.getItem(PREFIX + key);

    return value !== null && (options as ReadonlyArray<string>).includes(value) ? value as T : fallback;
  } catch {
    return fallback;
  }
}

export function saveChoicePreference(key: string, value: string): void {
  try {
    localStorage.setItem(PREFIX + key, value);
  } catch {
    // sem armazenamento: a preferência vale só nesta tela aberta
  }
}

/**
 * Preferência de id (09/10/2026, filtros de integração e de agente em Conversas).
 *
 * As opções chegam da API depois da tela abrir: quem lê confere o valor quando a
 * lista chegar e volta para "todos" se o item não existir mais.
 */
export function readTextPreference(key: string, fallback = ''): string {
  try {
    return localStorage.getItem(PREFIX + key) ?? fallback;
  } catch {
    return fallback;
  }
}

export function saveTextPreference(key: string, value: string): void {
  try {
    localStorage.setItem(PREFIX + key, value);
  } catch {
    // sem armazenamento: a preferência vale só nesta tela aberta
  }
}
